import * as React from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import TimePicker from "./Editor.TimePicker";
import { Button } from "ui/components/";
import useOnClickOutside from "ui/hooks/useOnClickOutside";
import styles from "./DateSelection.module.scss";
import "./DayPickerCustom.scss";

const formatDate = (date: Date): string => {
    const input = new Date(date);
    const today = new Date();
    if (input.setHours(0, 0, 0, 0) === today.setHours(0, 0, 0, 0)) {
        return "Today";
    }
    return input.toLocaleDateString("en-EN", {
        weekday: "short",
        month: "short",
        day: "numeric",
    });
};

interface Props {
    onlineMinutes: number;
    currentDate: Date;
    onSelectDate(newDate: Date): void;
}

const DateSelection: React.FC<Props> = ({
    onlineMinutes,
    currentDate,
    onSelectDate,
}) => {
    const [isDatePickerOpen, setIsDatePickerOpen] =
        React.useState<boolean>(false);
    const [dateStart, setDateStart] = React.useState<Date>(new Date());
    const [selectedDay, setSelectedDay] = React.useState<Date>(new Date());
    const datePickerRef = React.useRef<HTMLDivElement>(null);
    const dateEnd = new Date();
    useOnClickOutside(datePickerRef, () => setIsDatePickerOpen(false));

    React.useEffect(() => {
        const start = new Date(new Date().getTime() - onlineMinutes * 60000);
        setDateStart(start);
    }, [onlineMinutes]);

    const handleDayClick = (newDate: Date) => {
        setIsDatePickerOpen(false);
        newDate.setHours(
            selectedDay.getHours(),
            selectedDay.getMinutes(),
            0,
            0,
        );
        let selectedDate = newDate;
        if (newDate < dateStart) {
            selectedDate = new Date(dateStart);
            selectedDate.setMinutes(dateStart.getMinutes() + 5);
        }
        if (newDate > dateEnd) {
            selectedDate = new Date(dateEnd);
            selectedDate.setMinutes(+dateEnd.getMinutes() - 2);
        }
        setSelectedDay(selectedDate);
        onSelectDate(selectedDate);
    };

    return (
        <div className={styles.container}>
            <div className={styles.left}>
                <Button
                    type="secondary"
                    theme="light"
                    icon="calendar"
                    onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                >
                    {formatDate(currentDate)}
                </Button>
                {isDatePickerOpen && (
                    <div ref={datePickerRef}>
                        <DayPicker
                            className={styles.dayPicker}
                            disabled={{ before: dateStart, after: dateEnd }}
                            onDayClick={handleDayClick}
                            selected={currentDate}
                        />
                    </div>
                )}
            </div>
            <div className={styles.right}>
                <TimePicker
                    onChange={(time) => {
                        if (time < dateStart) {
                            onSelectDate(dateStart);
                        } else if (time > dateEnd) {
                            onSelectDate(dateEnd);
                        } else {
                            onSelectDate(time);
                        }
                    }}
                    timeStart={dateStart}
                    selectedDay={selectedDay}
                    timeEnd={dateEnd}
                    selectedTime={currentDate}
                />
            </div>
        </div>
    );
};

export default DateSelection;
